import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useProduction, useSeatMap } from '@/data/productions';
import { supabase } from '@/lib/supabase';
import { SeatMapRenderer } from '@/components/seatmap/SeatMapRenderer';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Empty } from '@/components/ui/Empty';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/productions/$id/seat-map')({
  component: SeatMapPage,
});

interface Section {
  id: string;
  name: string;
  row_count: number;
  seat_count_per_row: number;
  pricing_cents: number;
  display_order: number;
}

function useAddSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      production_id: string;
      name: string;
      row_count: number;
      seat_count_per_row: number;
      pricing_cents: number;
    }) => {
      // Single atomic RPC — assigns display_order monotonically, creates
      // the section, and generates seats in one transaction. A failure
      // rolls everything back so a section can never exist without seats.
      const { data, error } = await supabase.rpc('create_section_with_seats', {
        p_production_id: input.production_id,
        p_name: input.name,
        p_row_count: input.row_count,
        p_seat_count_per_row: input.seat_count_per_row,
        p_pricing_cents: input.pricing_cents,
      });
      if (error) throw error;
      return data; // section_id
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['seat-map'] });
    },
  });
}

function SeatMapPage() {
  const { id: productionId } = Route.useParams();
  const production = useProduction(productionId);
  const seatMap = useSeatMap(productionId);
  const addSection = useAddSection();

  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [rows, setRows] = useState(10);
  const [seatsPerRow, setSeatsPerRow] = useState(20);
  const [pricing, setPricing] = useState(2500);

  async function handleAdd() {
    try {
      await addSection.mutateAsync({
        production_id: productionId,
        name: name.trim(),
        row_count: rows,
        seat_count_per_row: seatsPerRow,
        pricing_cents: pricing,
      });
      setName('');
      setShowAdd(false);
      toast({ title: 'Section added', variant: 'success' });
    } catch (err) {
      toast({
        title: "Couldn't add section",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  const sections = (seatMap.data?.sections ?? []) as Section[];
  const seats = seatMap.data?.seats ?? [];

  return (
    <div className="space-y-8">
      <header>
        <Link
          to="/admin/productions/$id"
          params={{ id: productionId }}
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← {production.data?.name ?? 'Production'}
        </Link>
        <h1 className="mt-2">Seat map</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Configure sections + per-section pricing. Interactive seat selection is wired in Sprint 8
          alongside Stripe Checkout.
        </p>
      </header>

      <div className="flex justify-end">
        <Button onClick={() => setShowAdd((v) => !v)}>{showAdd ? 'Cancel' : 'Add section'}</Button>
      </div>

      {showAdd && (
        <section
          aria-labelledby="add-section-heading"
          className="surface-elevated grid gap-4 p-5 sm:grid-cols-4"
        >
          <h2 id="add-section-heading" className="sr-only">
            Add seat section
          </h2>
          <div>
            <Label htmlFor="sec-name">Name</Label>
            <Input id="sec-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="sec-rows">Rows</Label>
            <Input
              id="sec-rows"
              type="number"
              min="1"
              max="200"
              value={rows}
              onChange={(e) => setRows(Number.parseInt(e.target.value, 10) || 1)}
            />
          </div>
          <div>
            <Label htmlFor="sec-seats">Seats per row</Label>
            <Input
              id="sec-seats"
              type="number"
              min="1"
              max="200"
              value={seatsPerRow}
              onChange={(e) => setSeatsPerRow(Number.parseInt(e.target.value, 10) || 1)}
            />
          </div>
          <div>
            <Label htmlFor="sec-price">Price (cents)</Label>
            <Input
              id="sec-price"
              type="number"
              min="0"
              value={pricing}
              onChange={(e) => setPricing(Number.parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="sm:col-span-4">
            <Button
              onClick={() => void handleAdd()}
              disabled={!name.trim() || addSection.isPending}
            >
              {addSection.isPending ? 'Saving…' : 'Save section + generate seats'}
            </Button>
          </div>
        </section>
      )}

      {seatMap.isLoading && <p aria-live="polite">Loading…</p>}

      {sections.length === 0 && !seatMap.isLoading && (
        <Empty
          title="No seat sections yet"
          description="Add at least one section to render the seat map."
        />
      )}

      {sections.length > 0 && <SeatMapRenderer sections={sections} seats={seats} />}
    </div>
  );
}
